import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Tooltip, Tabs } from 'antd';
import { QuestionCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;

export interface TranslationOption {
  translatedText: string;
  sourceTrans: string;
  desc: string;
}

interface DemoTranslationOptionsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (option: TranslationOption) => void;
  options: TranslationOption[];
  title?: string; // context label
}

const DemoTranslationOptionsModal: React.FC<DemoTranslationOptionsModalProps> = ({
  visible,
  onCancel,
  onSave,
  options,
  title = 'Title',
}) => {
  const [selectedOption, setSelectedOption] = useState<TranslationOption | null>(null);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  useEffect(() => {
    setIsSaveEnabled(!!selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (!visible) {
      setSelectedOption(null);
    }
  }, [visible]);

  return (
    <Modal
      title="Select Translation Options"
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          onClick={() => selectedOption && onSave(selectedOption)}
          disabled={!isSaveEnabled}
        >
          Save
        </Button>,
      ]}
      width={'80%'}
      bodyStyle={{ padding: '12px 24px' }}
    >
      <Tabs defaultActiveKey="1" type="card" style={{ marginBottom: '8px' }}>
        <TabPane tab={title} key="1">
          <div
            style={{
              background: '#f7f7f7',
              padding: '8px 12px',
              borderRadius: '6px',
              marginBottom: '8px',
            }}
          >
            <div style={{ marginBottom: 0 }}>
              <strong>Select the best translation for your {title.toLowerCase()}:</strong>
              <Tooltip title="Choose the translation option that best captures the meaning and style.">
                <QuestionCircleOutlined style={{ color: '#1677ff', marginLeft: 8 }} />
              </Tooltip>
            </div>
          </div>

          {options.map((option, index) => (
            <Card
              key={index}
              style={{
                marginBottom: 10,
                border: selectedOption === option ? '2px solid #1677ff' : '1px solid #e8e8e8',
                borderRadius: '6px',
                boxShadow:
                  selectedOption === option ? '0 0 5px rgba(22, 119, 255, 0.3)' : 'none',
              }}
              onClick={() => setSelectedOption(option)}
              hoverable
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {selectedOption === option && (
                  <CheckCircleOutlined
                    style={{ color: '#1677ff', marginRight: '8px', marginTop: '2px' }}
                  />
                )}
                <div>
                  <p style={{ fontWeight: 'bold', fontSize: 15, marginBottom: 4 }}>
                    {option.translatedText}
                  </p>
                  <p style={{ marginBottom: 4 }}>
                    <strong>Meaning in original language:</strong> {option.sourceTrans}
                  </p>
                  <p style={{ color: '#666', marginBottom: 0 }}>
                    <strong>Description:</strong> {option.desc}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default DemoTranslationOptionsModal;
